import { type CSSProperties, type ReactNode } from "react";

/**
 * Lightweight Markdown renderer for long-text fields (no dependency). Supports
 * the subset that actually shows up in our content: **bold**, *italic*, `code`,
 * [text](url) + bare http(s) links, and numbered / bulleted lists. Everything
 * else is plain text. Read-only display — editing happens in a raw textarea.
 */

// One regex, alternated groups: IMAGE (must precede the link alt — it shares the
// [..](..) shape but with a leading !), bold, italic, code, md-link, bare-url.
const INLINE =
  /!\[([^\]]+)\]\([^)]+\)|\*\*([^*]+)\*\*|\*([^*\n]+)\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\)|(https?:\/\/[^\s)]+)/g;

// Decode the handful of HTML entities that leak into stored content (pasted from
// OneNote/Outlook, or emitted by the markdown serializer) so a table preview shows
// "->" not "-&gt;". `&amp;` is decoded LAST so "&amp;gt;" doesn't double-decode.
function decodeEntities(s: string): string {
  return s
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/&#x27;/gi, "'")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&");
}

// Clean one line for preview: decode entities + drop markdown escape backslashes
// (a trailing "\" is a hard-line-break marker; "\x" before punctuation is an
// escape) so neither leaks as a literal "\".
function cleanLine(s: string): string {
  return decodeEntities(s.replace(/\\+$/, "").replace(/\\([^\w\s])/g, "$1"));
}

const codeStyle: CSSProperties = {
  fontFamily: "JetBrains Mono Variable, ui-monospace, monospace",
  fontSize: "0.92em",
  background: "var(--surface-soft)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "0 4px",
};

// Image embeds (![alt](url)) are flattened to a compact muted chip showing the
// filename — never the raw "!alt" markdown the link regex used to leave behind.
const imgChipStyle: CSSProperties = {
  fontSize: "0.9em",
  color: "var(--ink-3)",
  background: "var(--surface-soft)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "0 5px",
};

function renderInline(rawText: string, kb: string): ReactNode[] {
  const text = cleanLine(rawText);
  const out: ReactNode[] = [];
  let last = 0;
  let k = 0;
  let m: RegExpExecArray | null;
  INLINE.lastIndex = 0;
  while ((m = INLINE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1] !== undefined) {
      out.push(
        <span key={`${kb}-${k++}`} style={imgChipStyle}>
          {m[1]}
        </span>,
      );
    } else if (m[2] !== undefined) {
      out.push(<strong key={`${kb}-${k++}`}>{m[2]}</strong>);
    } else if (m[3] !== undefined) {
      out.push(<em key={`${kb}-${k++}`}>{m[3]}</em>);
    } else if (m[4] !== undefined) {
      out.push(
        <code key={`${kb}-${k++}`} style={codeStyle}>
          {m[4]}
        </code>,
      );
    } else if (m[5] !== undefined) {
      out.push(
        <a
          key={`${kb}-${k++}`}
          href={m[6]}
          target="_blank"
          rel="noreferrer"
          style={{ color: "var(--accent)", wordBreak: "break-word" }}
        >
          {m[5]}
        </a>,
      );
    } else if (m[7] !== undefined) {
      out.push(
        <a
          key={`${kb}-${k++}`}
          href={m[7]}
          target="_blank"
          rel="noreferrer"
          style={{ color: "var(--accent)", wordBreak: "break-all" }}
        >
          {m[7]}
        </a>,
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// Compact single-line preview (table cells): renders bold/italic/strike/code
// inline, strips block markers (headings, blockquotes, list bullets, code
// fences, horizontal rules) so they never leak as raw characters, and shows
// link text plainly (no anchors, so the cell's own click handler isn't
// hijacked). Truncation is the caller's CSS.
const INLINE_PREVIEW =
  /!\[([^\]]+)\]\([^)]+\)|\*\*\*([^*]+)\*\*\*|\*\*([^*]+)\*\*|\*([^*\n]+)\*|~~([^~]+)~~|`([^`]+)`|\[([^\]]+)\]\([^)]+\)/g;

export function MarkdownInline({ text }: { text: string }) {
  const flat = text
    .split("\n")
    .map((raw) => {
      const line = raw.trim();
      // Horizontal rule (incl. spaced "- - -" / "* * *") — test BEFORE stripping
      // list markers, else the leading "- " is eaten first and the rule leaks.
      if (/^(?:[-*_] ?){3,}$/.test(line)) return "";
      // Structural strips run on the raw line (literal markdown markers); entity
      // decode + escape-strip happen AFTER so a decoded ">" isn't mistaken for a
      // blockquote marker.
      return cleanLine(
        line
          .replace(/^```.*$/, "") // code-fence lines
          .replace(/^#{1,6}\s+/, "") // headings
          .replace(/^>\s?/, "") // blockquote
          .replace(/^(?:\d+\.|[-*+])\s+/, "") // list markers
          .trim(),
      );
    })
    .filter(Boolean)
    .join(" · ");
  const out: ReactNode[] = [];
  let last = 0;
  let k = 0;
  let m: RegExpExecArray | null;
  INLINE_PREVIEW.lastIndex = 0;
  while ((m = INLINE_PREVIEW.exec(flat)) !== null) {
    if (m.index > last) out.push(flat.slice(last, m.index));
    if (m[1] !== undefined)
      out.push(
        <span key={k++} style={imgChipStyle}>
          {m[1]}
        </span>,
      );
    else if (m[2] !== undefined)
      out.push(
        <strong key={k++}>
          <em>{m[2]}</em>
        </strong>,
      );
    else if (m[3] !== undefined) out.push(<strong key={k++}>{m[3]}</strong>);
    else if (m[4] !== undefined) out.push(<em key={k++}>{m[4]}</em>);
    else if (m[5] !== undefined) out.push(<del key={k++}>{m[5]}</del>);
    else if (m[6] !== undefined)
      out.push(
        <code key={k++} style={codeStyle}>
          {m[6]}
        </code>,
      );
    else if (m[7] !== undefined) out.push(m[7]);
    last = m.index + m[0].length;
  }
  if (last < flat.length) out.push(flat.slice(last));
  return <>{out}</>;
}

export function MarkdownText({ text }: { text: string }) {
  const lines = text.split("\n");
  const blocks: ReactNode[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const flushList = (key: string) => {
    if (!list) return;
    const items = list.items.map((it, i) => (
      <li key={`${key}-li-${i}`} style={{ marginBottom: 2 }}>
        {renderInline(it, `${key}-${i}`)}
      </li>
    ));
    blocks.push(
      list.ordered ? (
        <ol
          key={`${key}-ol`}
          style={{
            margin: "2px 0 6px",
            paddingLeft: 22,
            listStyleType: "decimal",
            listStylePosition: "outside",
          }}
        >
          {items}
        </ol>
      ) : (
        <ul
          key={`${key}-ul`}
          style={{
            margin: "2px 0 6px",
            paddingLeft: 20,
            listStyleType: "disc",
            listStylePosition: "outside",
          }}
        >
          {items}
        </ul>
      ),
    );
    list = null;
  };

  lines.forEach((line, i) => {
    const ol = /^\s*\d+\.\s+(.*)$/.exec(line);
    const ul = /^\s*[-*]\s+(.*)$/.exec(line);
    if (ol) {
      if (!list || !list.ordered) flushList(`b${i}`);
      (list ??= { ordered: true, items: [] }).items.push(ol[1] ?? "");
    } else if (ul) {
      if (!list || list.ordered) flushList(`b${i}`);
      (list ??= { ordered: false, items: [] }).items.push(ul[1] ?? "");
    } else {
      flushList(`b${i}`);
      if (line.trim() === "") {
        blocks.push(<div key={`b${i}`} style={{ height: 6 }} />);
      } else {
        blocks.push(<div key={`b${i}`}>{renderInline(line, `b${i}`)}</div>);
      }
    }
  });
  flushList("end");

  return <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>{blocks}</div>;
}
